import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetServiceConditionQuery } from "@generated";
import { ServiceConditionForm } from "../ServiceConditionForm/ServiceConditionForm";
import { useServiceConditionMutations } from "../api";
import { emptyServiceConditionForm } from "../types";
import type { ServiceConditionFormValues } from "../types";

const FORM_ID = "service-condition-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useServiceConditionMutations();
  const { data, isLoading, refetch } = useV2GetServiceConditionQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ServiceConditionFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const attrs = data?.data?.attributes;

  const loadedInitial = useMemo<ServiceConditionFormValues>(() => {
    if (!attrs) return emptyServiceConditionForm;
    return {
      name: attrs.name ?? "",
      content: attrs.content ?? "",
      status: (attrs.status ?? "required") as "required" | "optional",
    };
  }, [attrs]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ServiceConditionFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      refetch();
    } catch (err) {
      console.error("Failed to update service condition", err);
      setToast({ variant: "danger", text: "Could not save." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Term or Condition"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ServiceConditionForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onInvalid={() =>
            setToast({
              variant: "danger",
              text: "Name and content must each be at least 3 characters.",
            })
          }
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
